/** angular */
import { Injectable } from '@angular/core';
import { BydHttpParams } from '@sixense/byd-core-service';
import { DocumentFilterInterface } from '@sixense/ds-ged-models';
import { Observable } from 'rxjs';
/** end angular */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
import { VisaReportDataInterface } from '../interfaces/visa-report-data.interface';
import { DsDocumentsExportResponse } from '../models/ds-documents-export-response.class';
/** end services */

@Injectable({
  providedIn: 'root',
})
export class DsDocumentsExportService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Starts export of all category documents. The result is sent by mail to the user (Excel export).
   *
   * @param withRevisions includes revisions
   * @param filter Search Filter
   * @returns  Observable<DsDocumentsExportResponse>
   */
  public startCategoryDocumentsExport(
    filter: DocumentFilterInterface,
    withRevisions = false
  ): Observable<DsDocumentsExportResponse> {
    this.path = `/nuxeo/v1/projects/${filter.projectId}/categories/${filter.categoryId}/documents?isglobalsearch=${withRevisions}`;
    return this.postModel<DsDocumentsExportResponse>({ body: filter });
  }

  /**
   * Starts export of all documents. The result is sent by mail to the user (Excel export).
   *
   * @param projectId Selected project id
   * @param withRevisions Includes revisions
   * @returns Observable<DsDocumentsExportResponse>
   */
  public startAllDocumentsExport(projectId: number, withRevisions = false): Observable<DsDocumentsExportResponse> {
    this.path = `/nuxeo/v1/projects/${projectId}/documents/globalexport?isglobalsearch=${withRevisions}`;
    return this.getModel<DsDocumentsExportResponse>();
  }

  /**
   * Start export of Visa.
   *
   * @param data Visa report data.
   * @returns Observable<DsDocumentsExportResponse>
   */
  public startVisaReport(data: VisaReportDataInterface): Observable<DsDocumentsExportResponse> {
    this.path = `/nuxeo/v1/projects/${data.projectId}/categories/${data.categoryId}/documents/${data.documentId}/circuits/${data.circuitId}`;
    return this.getModel<DsDocumentsExportResponse>();
  }

  /**
   * Starts export of all category documents. The result is sent by mail to the user (Excel export).
   *
   * @param withRevisions includes revisions
   * @param filter Search Filter
   * @param approbatorId Approbator ID
   * @returns  Observable<DsDocumentsExportResponse>
   */
  public startBulkVisasExport(
    filter: DocumentFilterInterface,
    approbatorId?: string
  ): Observable<DsDocumentsExportResponse> {
    this.path = `/nuxeo/v1/projects/${filter.projectId}/bulkfilevisas`;
    const params = approbatorId ? new BydHttpParams().set('approbator', approbatorId) : undefined;
    return this.postModel<DsDocumentsExportResponse>({ body: filter, params });
  }

  /**
   * Create DsDocumentsExportResponse instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsDocumentsExportResponse` instance.
   */
  public createNewInstance(jsonObject: DsDocumentsExportResponse) {
    return new DsDocumentsExportResponse(jsonObject);
  }
}
